import { createSlice } from '@reduxjs/toolkit'

export const initialState = {
  error: null,
  isFetchingUserNetSuiteAssets: false,
  isFetchingUserSites: false,
  netSuiteAssets: null,
  searchCriteria: null, // { key: value } pairs for searching assets
  sites: null,
}

// #region Helper functions
// Helper function to check if a preference matches the MSP Bullying Harassment Unit preference
const isMspBullyingHarassmentUnitPreference = preference =>
  preference?.accessCode === 'mspDigital' &&
  [
    'BullyingHarassmentUnitEnabled',
    'bullying_harassment_unit_enabled',
  ].includes(preference?.name)

// #endregion Helper functions

const slice = createSlice({
  name: 'ssapiManager',
  initialState,
  reducers: {
    clearErrors: state => {
      state.error = null
    },
    fetchingAllPublishFlows: state => {
      state.error = null
      state.isFetchingAllPublishFlows = true
    },
    fetchingAllPublishFlowsSuccess: (state, action) => {
      if (!Array.isArray(state.sites)) return
      const publishFlows = action.payload

      publishFlows.forEach(publishFlow => {
        const siteIndex = state.sites.findIndex(
          site => site?.id === publishFlow.siteId,
        )
        if (siteIndex !== -1) {
          const existingPublishFlows = Array.isArray(
            state.sites[siteIndex].publishFlows,
          )
            ? state.sites[siteIndex].publishFlows
            : []
          const publishFlowKey =
            publishFlow?.id ?? publishFlow?.publishFlowStructureId
          const hasPublishFlow = existingPublishFlows.some(existingFlow => {
            const existingKey =
              existingFlow?.id ?? existingFlow?.publishFlowStructureId
            return existingKey === publishFlowKey
          })

          state.sites[siteIndex] = {
            ...state.sites[siteIndex],
            publishFlows: hasPublishFlow
              ? existingPublishFlows
              : [...existingPublishFlows, publishFlow],
          }
        }
      })

      state.isFetchingAllPublishFlows = false
      state.error = null
    },
    fetchingAllPublishFlowsError: (state, action) => {
      state.error = action.payload
      state.isFetchingAllPublishFlows = false
    },
    fetchingPublishFlows: state => {
      state.error = null
      state.isFetchingPublishFlows = true
    },
    fetchingPublishFlowsSuccess: (state, action) => {
      const { data, siteId } = action.payload
      if (!Array.isArray(state.sites)) return

      const siteIndex = state.sites.findIndex(site => site?.id === siteId)
      if (siteIndex !== -1) {
        state.sites[siteIndex] = {
          ...state.sites[siteIndex],
          publishFlows: data,
        }
      }
      state.isFetchingPublishFlows = false
      state.error = null
    },
    fetchingPublishFlowsError: (state, action) => {
      state.error = action.payload
      state.isFetchingPublishFlows = false
    },
    postingPublishFlows: state => {
      state.error = null
      state.isPostingPublishFlows = true
    },
    postingPublishFlowsSuccess: (state, action) => {
      state.publishFlows = action.payload
      state.isPostingPublishFlows = false
      state.error = null
    },
    postingPublishFlowsError: (state, action) => {
      state.error = action.payload
      state.isPostingPublishFlows = false
    },
    updatingPublishFlows: state => {
      state.error = null
      state.isUpdatingPublishFlows = true
    },
    updatingPublishFlowsSuccess: (state, action) => {
      state.publishFlows = action.payload
      state.isUpdatingPublishFlows = false
      state.error = null
    },
    updatingPublishFlowsError: (state, action) => {
      state.error = action.payload
      state.isUpdatingPublishFlows = false
    },
    fetchingUserSites: state => {
      state.error = null
      state.isFetchingUserSites = true
    },
    fetchingUserSitesSuccess: (state, action) => {
      const incomingSites = action.payload

      if (!Array.isArray(incomingSites) || !Array.isArray(state.sites)) {
        state.sites = incomingSites
      } else {
        state.sites = incomingSites.map(incomingSite => {
          const existingSite = state.sites.find(
            site => site?.id === incomingSite?.id,
          )

          if (
            !existingSite ||
            (incomingSite.publishFlows !== undefined &&
              incomingSite.publishFlows !== null)
          ) {
            return incomingSite
          }

          return {
            ...incomingSite,
            publishFlows: existingSite.publishFlows,
          }
        })
      }

      state.isFetchingUserSites = false
      state.error = null
    },
    fetchingUserSitesError: (state, action) => {
      state.error = action.payload
      state.isFetchingUserSites = false
    },
    fetchingUserNetSuiteAssets: state => {
      state.error = null
      state.isFetchingUserNetSuiteAssets = true
    },
    fetchingUserNetSuiteAssetsSuccess: (state, action) => {
      state.netSuiteAssets = Object.assign(
        {},
        state.netSuiteAssets,
        Object.fromEntries(
          action.payload?.map(asset => [asset.id, asset]) || [],
        ),
      )
      state.isFetchingUserNetSuiteAssets = false
      state.error = null
    },
    fetchingUserNetSuiteAssetsError: (state, action) => {
      state.error = action.payload
      state.isFetchingUserNetSuiteAssets = false
    },
    setSearchQuery: (state, action) => {
      if (
        action.payload === null ||
        (action.payload.key === undefined && action.payload.value === null)
      ) {
        // Clear all search criteria
        state.searchCriteria = null
      } else if (action.payload.key && action.payload.value !== undefined) {
        // Set specific key-value pair
        state.searchCriteria = {
          ...state.searchCriteria,
          [action.payload.key]: action.payload.value,
        }
      }
    },
    setStructureVersion: (state, action) => {
      state.structureVersion = action.payload
    },
    updateSitePacingDates: (state, action) => {
      const {
        siteId,
        pacingDates,
        programName,
        schoolYearEndDateString,
      } = action.payload
      if (!Array.isArray(state.sites)) return

      const siteIndex = state.sites.findIndex(site => site?.id === siteId)
      if (siteIndex !== -1) {
        const site = state.sites[siteIndex]
        state.sites[siteIndex] = {
          ...site,
          pacing: {
            ...(site.pacing || {}),
            [programName]: pacingDates,
          },
          schoolYearEndDate: schoolYearEndDateString,
        }
      }
    },
    updateSitesMspOptIn: (state, action) => {
      const { payload: createdItems } = action
      if (
        !Array.isArray(state.sites) ||
        !Array.isArray(createdItems) ||
        createdItems.length === 0
      ) {
        return state
      }

      state.sites = state.sites.map(site => {
        const matchingItem = createdItems.find(item => item.siteId === site.id)

        if (!matchingItem || !Array.isArray(site.preferences)) {
          return site
        }

        const newPreference = matchingItem.preferences?.find(
          isMspBullyingHarassmentUnitPreference,
        )

        if (!newPreference) {
          return site
        }

        return {
          ...site,
          preferences: site.preferences.map(preference =>
            isMspBullyingHarassmentUnitPreference(preference)
              ? { ...preference, ...newPreference }
              : preference,
          ),
        }
      })
    },
    updateSitePreferences: (state, action) => {
      const { siteId, preferences } = action.payload
      if (!Array.isArray(state.sites) || !Array.isArray(preferences)) return

      const siteIndex = state.sites.findIndex(site => site?.id === siteId)
      if (siteIndex !== -1) {
        state.sites[siteIndex] = {
          ...state.sites[siteIndex],
          preferences,
        }
      }
    },
  },
})

export const actions = slice.actions
export default slice.reducer
